import { Document, ObjectId } from 'mongodb';
import { ConnectionInfo } from '@/methods/types';

export interface UserEmail {
  address: string;
  verified: boolean;
}

export interface User extends Document {
  _id: ObjectId;

  handle: string;

  emails?: UserEmail[];

  status?: 'active' | 'disabled' | 'deleted';

  firstName?: string;
  lastName?: string;
  avatarUrl?: string;

  createdAt: Date;
  disabledAt?: Date;
  deletedAt?: Date;

  roles?: string[];

  authMethods: {
    password?: {
      hash: string;
    };
    google?: {
      id: string;
    };
    github?: {
      id: string;
    };
  };
}

export type UserInfo = {
  /** The user's unique identifier. */
  id: string;
  /** The user's display handle. */
  handle: string;
  /** The role strings assigned to this user in the database. */
  roles: string[];
  /** Returns `true` if the user has the given role. */
  hasRole: (role: string) => boolean;
  /** Throws an error if the user does not have the given role. */
  requireRole: (role: string) => void;
  firstName?: string;
  lastName?: string;
  avatarUrl?: string;
};

export type Role = string;

export type DefaultRoles = Record<'authenticated' | 'unauthenticated', Role | null>;

export type Session = {
  authToken: string;
  expiresAt: Date;
  userId: ObjectId | null;
};

export type Permission = string;

/**
 * Defines a role that can be assigned to users.
 *
 * Roles are registered via the `roles` option in {@link AppOptions} and
 * are synced to the Modelence Cloud dashboard for user management.
 *
 * @example
 * ```typescript
 * import { startApp } from 'modelence/server';
 *
 * startApp({
 *   roles: {
 *     admin: { description: 'Full access to all features' },
 *     editor: { description: 'Can edit content' },
 *     viewer: {},
 *   },
 * });
 * ```
 */
export type RoleDefinition = {
  /** Human-readable description of the role, shown in the Modelence Cloud dashboard. */
  description?: string;
  /** @internal */
  permissions?: Permission[];
};

export const SUPPORTED_OAUTH_PROVIDERS = ['google', 'github'] as const;
export type OAuthProvider = (typeof SUPPORTED_OAUTH_PROVIDERS)[number];

export type AuthProvider = OAuthProvider | 'email' | 'magicLink';

export type AuthSuccessProps = {
  provider: AuthProvider;
  user: User;
  session: Session | null;
  connectionInfo: ConnectionInfo;
};

export type AuthErrorProps = {
  provider: AuthProvider;
  error: Error;
  session: Session | null;
  connectionInfo: ConnectionInfo;
};

export type OAuthErrorInfo = {
  error: string;
  statusCode: number;
};
